import type { ReactNode } from 'react';

/**
 * Small stroke icons drawn in `currentColor`, so the merchant's text colours apply to them too.
 * The first group is what a reason or resolution can pick in the config (`ICONS`); the rest are ours.
 */
const PATHS = {
  box: (
    <>
      <path d="M3.5 7.5 12 3l8.5 4.5v9L12 21l-8.5-4.5Z" />
      <path d="M3.5 7.5 12 12l8.5-4.5M12 12v9M7.8 5.2l8.5 4.6" />
    </>
  ),
  pin: (
    <>
      <path d="M12 21s-7-6.2-7-11.5a7 7 0 0 1 14 0C19 14.8 12 21 12 21Z" />
      <path d="M10.2 8.2a1.9 1.9 0 1 1 2.6 1.8c-.5.2-.8.6-.8 1.1v.4M12 13.8h.01" />
    </>
  ),
  alert: <path d="M12 3 21 19H3ZM12 9.5V14M12 16.8h.01" />,
  dots: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M8.5 12h.01M12 12h.01M15.5 12h.01" strokeWidth="2.6" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.5V12l3 2" />
    </>
  ),
  money: (
    <>
      <path d="M4 12a8 8 0 1 0 2.4-5.7M4 4v4.5h4.5" />
      <path d="M12 8v8M14.2 9.6c-.4-.7-1.2-1.1-2.2-1.1-1.3 0-2.2.7-2.2 1.7 0 2.3 4.6 1.2 4.6 3.6 0 1-1 1.7-2.4 1.7-1 0-1.9-.4-2.3-1.2" />
    </>
  ),
  gift: (
    <>
      <path d="M4 10.5h16V20H4ZM3 7.5h18v3H3ZM12 7.5V20" />
      <path d="M12 7.5S10.8 4 8.8 4a2 2 0 0 0 0 3.5ZM12 7.5S13.2 4 15.2 4a2 2 0 0 1 0 3.5Z" />
    </>
  ),
  ticket: (
    <>
      <path d="M3.5 8.5A2 2 0 0 0 5.5 6.5h13a2 2 0 0 0 2 2v1.5a2 2 0 0 0 0 4V17a2 2 0 0 0-2-2h-13a2 2 0 0 0-2 2v-2.9a2 2 0 0 0 0-4Z" />
      <path d="M12 8.5v2M12 13.5v2" />
    </>
  ),
  check: <path d="M5 12.5l4.5 4.5L19 7.5" strokeWidth="2.4" />,
  question: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M9.6 9.3a2.5 2.5 0 1 1 3.4 2.4c-.7.3-1 .8-1 1.5v.4M12 16.6h.01" />
    </>
  ),

  upload: <path d="M12 15V4M7.5 8.5 12 4l4.5 4.5M4 15v3.5A1.5 1.5 0 0 0 5.5 20h13a1.5 1.5 0 0 0 1.5-1.5V15" />,
  file: <path d="M6 3.5h8l4 4v13H6ZM14 3.5v4h4" />,
  info: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v5M12 7.8h.01" strokeWidth="2.2" />
    </>
  ),
  chevron: <path d="M9 18l6-6-6-6" strokeWidth="2" />,
  close: <path d="M7 7l10 10M17 7 7 17" strokeWidth="2" />,
} satisfies Record<string, ReactNode>;

export type IconName = keyof typeof PATHS;

export function Icon({ name, className }: { name: IconName; className?: string }) {
  return (
    <svg
      className={className ? `nvd-claim-portal__icon ${className}` : 'nvd-claim-portal__icon'}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {PATHS[name]}
    </svg>
  );
}
